import brouterProfilesData from '@/config/brouter-profiles.json'
import { toursTrackType } from '@/masterdata/tours_tracktype'
import type { Track } from '@/types/tour'

export type RoutingProfileSource = 'upstream' | 'custom'

export interface RoutingProfileDefinition {
  id: string
  label: string
  brouterProfile: string
  source: RoutingProfileSource
}

export interface RoutingProfile {
  id: string
  label: string
}

export const DEFAULT_ROUTING_PROFILE = 'trekking'

/**
 * "Fake" profile that does not route through BRouter at all. It simply connects
 * the given points with straight lines (point A → point B). Handled locally in
 * the routing service instead of being sent to the BRouter backend.
 */
export const STRAIGHT_LINE_ROUTING_PROFILE = 'plane'

export const isStraightLineRoutingProfile = (profileId: string | null | undefined): boolean =>
  profileId === STRAIGHT_LINE_ROUTING_PROFILE

export const ROUTING_PROFILE_DEFINITIONS = brouterProfilesData as RoutingProfileDefinition[]

export const ROUTING_PROFILES: RoutingProfile[] = ROUTING_PROFILE_DEFINITIONS.map(
  ({ id, label }) => ({ id, label }),
)

const routingProfileDefinitionsById = new Map(
  ROUTING_PROFILE_DEFINITIONS.map((profile) => [profile.id, profile] as const),
)
const knownRoutingProfileIds = new Set(ROUTING_PROFILE_DEFINITIONS.map((profile) => profile.id))
const trackTypeCategoryByGarminId = new Map(
  Object.values(toursTrackType).flatMap((category) =>
    Object.values(category).map((trackType) => [trackType.garmin_id, trackType.category] as const),
  ),
)
const trackTypeRoutingProfileOverrides = new Map<string, string>([
  ['road_biking', 'fastbike'],
  ['track_cycling', 'fastbike'],
  ['gravel_cycling', 'gravel'],
  ['cyclocross', 'gravel'],
  ['mountain_biking', 'mtb'],
  ['downhill_mountain_biking', 'mtb'],
  ['enduro_mountain_biking', 'mtb'],
  ['emountain_biking', 'mtb'],
  ['eenduro_mountain_biking', 'mtb'],
  ['hiking', 'hiking-beta'],
  ['rucking', 'hiking-beta'],
  ['mountaineering', 'hiking-beta'],
  ['car', 'car-fast'],
  ['plane', STRAIGHT_LINE_ROUTING_PROFILE],
  ['flight', STRAIGHT_LINE_ROUTING_PROFILE],
  ['rail', 'rail'],
])
const hikingLikeTrackTypeCategories = new Set(['hiking', 'walking', 'running'])

const defaultRoutingProfileDefinition = routingProfileDefinitionsById.get(DEFAULT_ROUTING_PROFILE)

if (!defaultRoutingProfileDefinition) {
  throw new Error(`Missing default routing profile definition for "${DEFAULT_ROUTING_PROFILE}"`)
}

export const isKnownRoutingProfileId = (
  profileId: string | null | undefined,
): profileId is string => !!profileId && knownRoutingProfileIds.has(profileId)

export const getRoutingProfileDefinition = (profileId: string | null | undefined) =>
  (profileId ? routingProfileDefinitionsById.get(profileId) : undefined) ??
  defaultRoutingProfileDefinition

export const resolveBrouterProfileId = (profileId: string | null | undefined) =>
  getRoutingProfileDefinition(profileId).brouterProfile

export const resolveTrackTypeRoutingProfileId = (trackType: string | null | undefined): string => {
  if (!trackType) return DEFAULT_ROUTING_PROFILE

  const override = trackTypeRoutingProfileOverrides.get(trackType)
  if (override) return override

  if (trackType.includes('mountain_biking')) {
    return 'mtb'
  }

  const category = trackTypeCategoryByGarminId.get(trackType)
  if (category && hikingLikeTrackTypeCategories.has(category)) {
    return 'hiking-beta'
  }

  if (category === 'cycling') {
    return 'trekking'
  }

  if (category === 'driving') {
    return 'car-fast'
  }

  if (category === 'aviation') {
    return STRAIGHT_LINE_ROUTING_PROFILE
  }

  if (category === 'rail') {
    return 'rail'
  }

  return DEFAULT_ROUTING_PROFILE
}

type RoutingProfileTrack = Pick<Track, 'routingProfile' | 'type'>

export const resolveTrackRoutingProfileId = (
  track: RoutingProfileTrack | null | undefined,
  tourRoutingProfile?: string | null,
): string => {
  if (isKnownRoutingProfileId(track?.routingProfile)) {
    return track.routingProfile
  }

  if (isKnownRoutingProfileId(tourRoutingProfile)) {
    return tourRoutingProfile
  }

  return resolveTrackTypeRoutingProfileId(track?.type)
}
